/**
 * Cache policy: bounded IndexedDB usage without ever losing what the user is looking at.
 *
 * - messages: at most 5,000 rows per folder; beyond that the oldest (by date) go first, but
 *   unread and flagged rows are evicted last because they are the ones a user comes back for.
 * - bodies: at most 300 entries / 40 MB in total, LRU by `fetchedAt` (`touchBody()` on read).
 * - contacts: unbounded (a few hundred small rows).
 * `pruneCaches()` runs at boot and every 10 minutes (`startPruneTimer()`).
 */
import { db } from './index';
import type { BodyRec, MessageRow } from '$lib/types';

export const MESSAGES_PER_FOLDER_CAP = 5000;
export const BODIES_CAP = 300;
export const BODIES_BYTES_CAP = 40 * 1024 * 1024;
export const PRUNE_INTERVAL_MS = 10 * 60e3;

export interface PruneReport {
  messagesEvicted: number;
  bodiesEvicted: number;
  foldersTouched: string[];
}

/** Size estimate of a body row (UTF-16 → ~2 bytes/char is pessimistic; use 1.5). */
export function bodyBytes(b: Pick<BodyRec, 'content' | 'meta'>): number {
  return Math.ceil(((b.content?.length ?? 0) + (b.meta?.length ?? 0)) * 1.5);
}

const isProtected = (r: MessageRow) => !r.flags.includes('\\Seen') || r.flags.includes('\\Flagged');
const timeOf = (r: MessageRow) => Date.parse(r.receivedAt ?? r.date) || 0;

/**
 * Choose which rows to evict from one folder to respect `cap`: sort so that protected rows
 * (unread/flagged) come last and, within each class, newest first; evict the tail.
 */
export function chooseMessageEvictions(rows: readonly MessageRow[], cap = MESSAGES_PER_FOLDER_CAP): MessageRow[] {
  if (rows.length <= cap) return [];
  const ordered = [...rows].sort((a, b) => {
    const pa = isProtected(a) ? 1 : 0;
    const pb = isProtected(b) ? 1 : 0;
    if (pa !== pb) return pa - pb; // unprotected first (they are evicted first)
    return timeOf(a) - timeOf(b); // oldest first
  });
  return ordered.slice(0, rows.length - cap);
}

/** Choose bodies to drop: LRU by `fetchedAt` until both the count and byte caps hold. */
export function chooseBodyEvictions(bodies: readonly BodyRec[], caps = { count: BODIES_CAP, bytes: BODIES_BYTES_CAP }): BodyRec[] {
  const ordered = [...bodies].sort((a, b) => (a.fetchedAt || 0) - (b.fetchedAt || 0));
  let count = ordered.length;
  let bytes = ordered.reduce((s, b) => s + bodyBytes(b), 0);
  const out: BodyRec[] = [];
  for (const b of ordered) {
    if (count <= caps.count && bytes <= caps.bytes) break;
    out.push(b);
    count--;
    bytes -= bodyBytes(b);
  }
  return out;
}

export async function pruneMessages(cap = MESSAGES_PER_FOLDER_CAP): Promise<{ evicted: number; folders: string[] }> {
  const folders = (await db.messages.orderBy('folder').uniqueKeys()) as string[];
  let evicted = 0;
  const touched: string[] = [];
  for (const folder of folders) {
    const count = await db.messages.where('folder').equals(folder).count();
    if (count <= cap) continue;
    const rows = await db.messages.where('folder').equals(folder).toArray();
    const victims = chooseMessageEvictions(rows, cap);
    if (!victims.length) continue;
    const keys = victims.map((r) => r.key);
    await db.messages.bulkDelete(keys);
    await db.bodies.bulkDelete(keys);
    evicted += keys.length;
    touched.push(folder);
  }
  return { evicted, folders: touched };
}

export async function pruneBodies(caps = { count: BODIES_CAP, bytes: BODIES_BYTES_CAP }): Promise<number> {
  const all = await db.bodies.toArray();
  const victims = chooseBodyEvictions(all, caps);
  if (victims.length) await db.bodies.bulkDelete(victims.map((b) => b.key));
  return victims.length;
}

/** Run every policy once. Never throws — a failed prune must not break the app. */
export async function pruneCaches(): Promise<PruneReport> {
  const report: PruneReport = { messagesEvicted: 0, bodiesEvicted: 0, foldersTouched: [] };
  try {
    const m = await pruneMessages();
    report.messagesEvicted = m.evicted;
    report.foldersTouched = m.folders;
    report.bodiesEvicted = await pruneBodies();
  } catch {
    /* IndexedDB hiccup: the next run will retry */
  }
  return report;
}

/** Mark a body as recently used (LRU clock). */
export async function touchBody(key: string, now: number = Date.now()): Promise<void> {
  try {
    await db.bodies.update(key, { fetchedAt: now });
  } catch {
    /* ignore */
  }
}

let pruneTimer: ReturnType<typeof setInterval> | null = null;

/** Prune now and then every `PRUNE_INTERVAL_MS`. Idempotent. */
export function startPruneTimer(intervalMs = PRUNE_INTERVAL_MS): void {
  if (pruneTimer) return;
  void pruneCaches();
  pruneTimer = setInterval(() => void pruneCaches(), intervalMs);
}

export function stopPruneTimer(): void {
  if (pruneTimer) clearInterval(pruneTimer);
  pruneTimer = null;
}
